<template>
  <div ref="root" :class="actionClasses" class="ak-attached-actions__item">
    <slot />
  </div>
</template>

<script>
import { computed, defineComponent, onMounted, ref } from 'vue'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-attached-action position="left"><a href="#">Link</a></ak-attached-action>
   */

  name: 'AkAttachedAction',

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    /**
     * @type {String}
     * @description The parent container to attach actions to
     */
    container: {
      type: String,
      required: true,
    },

    /**
     * @type {String}
     * @description This controls the position of the action
     */
    position: {
      type: String,
      default: 'right',
    },

    /**
     * @type {String}
     * @description Display on the edge of the parent container instead of outside
     */
    overlap: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {String}
     * @description Display a shadow around the container on hover
     */
    hasShadow: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {String}
     * @description This controls the alignment of content inside the action
     */
    alignment: {
      validator: (v) => ['start', 'center', 'end'].indexOf(v) >= 0,
      type: String,
      default: undefined,
    },

    /**
     * @type {Boolean}
     * @description Determines whether the zindex is high or not
     * used for nested attached actions when you want one in front of another
     */
    stacked: {
      type: Boolean,
      default: undefined,
    },

    /**
     * @type {String}
     * @description Whether or not the parent is hoverable to display the action
     * if false, the action will always be visible
     */
    hover: {
      type: Boolean,
      default: true,
    },
  },

  /**
   * @param {Object} props - The properties of the component
   * @description Sets up the component and returns an object containing all of the
   * attributes for the component to use.
   */
  setup(props) {
    const root = ref(null)

    const actionClasses = computed(() => {
      return {
        [`ak-attached-actions__item--${props.position}`]: props.position,
        [`ak-attached-actions__item--align-${props.alignment}`]:
          props.alignment,
        [`ak-attached-actions__item--overlap`]: props.overlap,
        [`ak-attached-actions__item--stacked`]: props.stacked,
      }
    })

    onMounted(() => {
      const containerEl =
        document.querySelector(props.container) || root.value.parentNode
      /* istanbul ignore else */
      if (containerEl) {
        containerEl.classList.add('ak-attached-actions')
        if (props.hover) containerEl.classList.add('ak-attached-actions--hover')
        if (props.hasShadow)
          containerEl.classList.add('ak-attached-actions--shadow')
      } else {
        throw new Error(`element "${props.container}" not found`)
      }
    })

    return {
      actionClasses,
      root,
    }
  },
})
</script>

<style lang="scss">
@import '../../scss/_settings.scss';
.ak-attached-actions {
  position: relative;
  transition: $transition;
  &__item {
    position: absolute;
    z-index: 0;
    top: 0;
    bottom: 0;
    display: flex;

    &--stacked {
      z-index: 5;
    }

    &--top {
      bottom: 100%;
      top: auto;
      left: 0;
      right: 0;
      padding-bottom: $spacing-tiny;
      flex-direction: column;
      align-items: center;
      &.ak-attached-actions__item--overlap {
        bottom: calc(100% - 1rem);
      }
    }
    &--bottom {
      top: 100%;
      bottom: auto;
      left: 0;
      right: 0;
      padding-top: $spacing-tiny;
      flex-direction: column;
      align-items: center;
      &.ak-attached-actions__item--overlap {
        top: calc(100% - 1rem);
      }
    }
    &--left {
      right: 100%;
      padding-right: $spacing-tiny;
      &.ak-attached-actions__item--overlap {
        right: calc(100% - 1rem);
      }
    }
    &--right {
      left: 100%;
      padding-left: $spacing-tiny;
      &.ak-attached-actions__item--overlap {
        left: calc(100% - 1rem);
      }
    }

    &--align-start {
      align-items: flex-start;
    }
    &--align-center {
      align-items: center;
    }
    &--align-end {
      align-items: flex-end;
    }
  }

  &--hover {
    > .ak-attached-actions__item {
      transition: opacity 0.2s ease;
      will-change: opacity;
      opacity: 0;
    }

    &:hover {
      z-index: 5;
      > .ak-attached-actions__item {
        opacity: 1;
      }
    }
    > .ak-attached-actions__item {
      &.is-active {
        opacity: 1;
      }
    }
  }

  &--shadow {
    &:hover {
      box-shadow: $base-shadow-huge;
      z-index: 5;
    }
  }
}
</style>
